import { Euler, Vector3 } from 'three';
import { resolveItemContour } from './itemContour.js';
export function operationCaptionTargetPoints(frame, targetKeys) {
    const keys = frame.itemCollections.flatMap(collection => collection.itemCollectionKind === 'entity'
        ? collection.items.filter(item => targetKeys.includes(item.itemKey)).map(item => item.itemKey) : []);
    return keys.map(key => {
        const contour = resolveItemContour(frame, key, '');
        if (!contour || !('points' in contour))
            throw new Error(`Operation caption target '${key}' requires a panel outline.`);
        const rotation = new Euler(...contour.rotation);
        const position = new Vector3(...contour.position);
        return contour.points.map(point => new Vector3(...point).applyEuler(rotation).add(position).toArray());
    });
}
function captionRows(text, columns) {
    const rows = [];
    let remaining = text;
    while (remaining.length > columns) {
        const whitespace = remaining.lastIndexOf(' ', columns);
        const end = whitespace > 0 ? whitespace : columns;
        rows.push(remaining.slice(0, end));
        remaining = remaining.slice(end).trimStart();
    }
    if (remaining)
        rows.push(remaining);
    return rows;
}
export function placeOperationCaption(width, height, text, protectedAreas, layoutText = text) {
    if (![width, height].every(value => Number.isFinite(value) && value > 0) || !text.trim()) {
        throw new Error('Operation caption placement requires a viewport and nonempty text.');
    }
    const left = width > 620 ? 270 : 12;
    const top = width > 620 ? 18 : 280;
    const right = width - 18, bottom = height - 190;
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    let partialPlacement;
    for (const fontPixels of [20, 18, 16, 14]) {
        const captionWidth = Math.min(right - left, 480);
        const columns = Math.floor((captionWidth - 24) / (fontPixels * 0.7));
        if (columns < 1)
            continue;
        const rows = captionRows(text, columns);
        const rowCount = Math.max(rows.length, captionRows(layoutText, columns).length);
        const captionHeight = rowCount * fontPixels * 1.3 + 20;
        const centerX = clamp(width / 2 - captionWidth / 2, left, right - captionWidth);
        const centerY = clamp(height / 2 + 65, top, bottom - captionHeight);
        const xs = [centerX, right - captionWidth, left,
            ...protectedAreas.flatMap(area => [area.left - captionWidth - 16, area.right + 16])];
        const ys = [centerY, height / 2 - 65 - captionHeight, top, bottom - captionHeight,
            ...protectedAreas.flatMap(area => [area.top - captionHeight - 16, area.bottom + 16])];
        const candidates = ys.flatMap(y => xs.map(x => ({
            left: x, top: y, right: x + captionWidth, bottom: y + captionHeight,
        }))).filter(rectangle => rectangle.left >= left && rectangle.right <= right
            && rectangle.top >= top && rectangle.bottom <= bottom);
        candidates.sort((a, b) => Math.hypot(a.left - centerX, a.top - centerY) - Math.hypot(b.left - centerX, b.top - centerY));
        const placement = candidates.find(rectangle => !protectedAreas.some(area => rectangle.left < area.right + 12 && rectangle.right > area.left - 12
            && rectangle.top < area.bottom + 12 && rectangle.bottom > area.top - 12));
        if (placement)
            return { ...placement, fontPixels, rows, rowCount, columns };
        const partial = candidates.find(rectangle => {
            if (rectangle.left < width / 2 + 60 && rectangle.right > width / 2 - 60
                && rectangle.top < height / 2 + 45 && rectangle.bottom > height / 2 - 45)
                return false;
            return protectedAreas.every(area => {
                const overlap = Math.max(0, Math.min(rectangle.right, area.right) - Math.max(rectangle.left, area.left))
                    * Math.max(0, Math.min(rectangle.bottom, area.bottom) - Math.max(rectangle.top, area.top));
                return overlap <= (area.right - area.left) * (area.bottom - area.top) * 0.25;
            });
        });
        if (!partialPlacement && partial)
            partialPlacement = { ...partial, fontPixels, rows, rowCount, columns };
    }
    return partialPlacement;
}
